import React, { useContext, useEffect, useRef, useState } from "react";

import { useMutation } from "@apollo/client";
import { sendCode, sendCodeVariables } from "@layout/modals/graphql/__generated__/sendCode";
import { SEND_CODE } from "@layout/modals/graphql/SEND_CODE";
import { FormTypeEnum, LoginChangeForm, LoginTypeEnum } from "@layout/modals/types";
import { getErrorByCode, setAuthParamToURL } from "@layout/modals/utils";
import { useLocation } from "@reach/router";
import _uniqueId from "lodash/uniqueId";

import { isEmailValid } from "@/components/common/Inputs/emailInput/utils";
import { ErrorCodeEnum } from "@/constants";
import AuthorizationFormContext from "@/contexts/AuthorizationForm/AuthorizationFormContext";
import { useSmartCaptcha } from "@/contexts/SmartCaptcha/useSmartCaptcha";
import UserContext from "@/contexts/User/UserContext";
import { useManageUtmData } from "@/hooks/useManageUtmData";
import { getErrorCode } from "@/utils/apolloUtils";
import { trimAndLowerCase } from "@/utils/stringUtils";
import { Button, ButtonColorEnum, ButtonSizeEnum } from "@components/common/button";
import { SEND_OTP_CODE_RETRY_DELAY } from "@components/constants";

const LoginByEmailModal = (props: LoginChangeForm) => {
  const { clientReferrer } = useContext(UserContext);

  const { setIsFirstLoginCallback } = props;
  const location = useLocation();
  const [sendCodeMutation, { data, loading, error }] = useMutation<sendCode, sendCodeVariables>(
    SEND_CODE,
    {
      fetchPolicy: "network-only",
    },
  );
  const [errorCode, setErrorCode] = useState<number | null>(null);

  const [email, setEmail] = useState("");
  const [isEmailTouched, setIsEmailTouched] = useState<boolean>(false);
  const { sentCodeTimestamp, setSentCodeTimestamp, setSentContact } = useContext(AuthorizationFormContext);
  const [inputUniqueId] = useState(_uniqueId());
  // Почта из последнего запроса sendCode; попадает в контекст, когда бэк подтвердил отправку кода
  const requestedEmailRef = useRef("");

  const { getUtmData } = useManageUtmData();
  const { executeCaptcha, prepareCaptcha } = useSmartCaptcha();

  // Капча монтируется только когда нужна (SEO #12): подгружаем её при открытии формы,
  // чтобы к отправке внешний скрипт уже был готов и пользователь не ждал.
  useEffect(() => {
    prepareCaptcha();
  }, [prepareCaptcha]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setEmail(value);
    if (errorCode) {
      setErrorCode(null);
    }
  };

  const handleSubmit = async () => {
    if (!isEmailValid(email)) {
      return;
    }

    const yandexCaptchaToken = await executeCaptcha();
    if (!yandexCaptchaToken) {
      setErrorCode(ErrorCodeEnum.StatusCodeReCaptchaError);
    } else {
      requestedEmailRef.current = trimAndLowerCase(email);
      sendCodeMutation({
        variables: {
          email: requestedEmailRef.current,
          utm: getUtmData(),
          reCaptcha: yandexCaptchaToken,
          referer: clientReferrer,
        },
      });
    }
  };

  const onKeyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.code === "Enter" || event.code === "NumpadEnter") {
      event.preventDefault();
      setIsEmailTouched(true);
      handleSubmit();
    }
  };

  useEffect(() => {
    if (data && !loading && !error) {
      setErrorCode(null);
      setSentCodeTimestamp(Date.now() + SEND_OTP_CODE_RETRY_DELAY);
      setSentContact({ loginType: LoginTypeEnum.LoginByEmail, value: requestedEmailRef.current });
      if (setIsFirstLoginCallback) {
        setIsFirstLoginCallback(!!data.sendCode);
      }
      setAuthParamToURL(location, FormTypeEnum.CodeFromMail);
    }
  }, [data, error, loading, location, setIsFirstLoginCallback, setSentCodeTimestamp, setSentContact]);

  useEffect(() => {
    if (error) {
      // If we get the StatusCodeTooManyRequests provide user to form with code
      if (getErrorCode(error) === ErrorCodeEnum.StatusCodeTooManyRequests) {
        if (!sentCodeTimestamp) {
          setSentCodeTimestamp(Date.now() + SEND_OTP_CODE_RETRY_DELAY);
        }
        setSentContact({ loginType: LoginTypeEnum.LoginByEmail, value: requestedEmailRef.current });
        if (setIsFirstLoginCallback) {
          setIsFirstLoginCallback(!!data?.sendCode);
        }
        setAuthParamToURL(location, FormTypeEnum.CodeFromMail);
      } else {
        setErrorCode(getErrorCode(error));
      }
    }
  }, [error, data, location, sentCodeTimestamp, setSentCodeTimestamp, setSentContact, setIsFirstLoginCallback]);

  return (
    <>
      <div className="input-group">
        <input
          type="text"
          id={inputUniqueId}
          value={email}
          autoFocus
          onChange={handleChange}
          onKeyDown={onKeyDownHandler}
          onBlur={() =>
            setIsEmailTouched(true)}
          placeholder="maria@mail.ru"
        />

        {errorCode && <div className="input-group__error">{getErrorByCode(errorCode)}</div>}
        {/* todo: reuse EmailBlock */}
        {isEmailTouched && email && !isEmailValid(email) && (
          <div className="input-group__error">Введите e-mail в формате maria@mail.ru</div>
        )}
      </div>
      <Button
        text="Получить код в письме"
        isLoading={loading}
        size={ButtonSizeEnum.Large}
        color={ButtonColorEnum.Dark}
        onClick={handleSubmit}
        disabled={!isEmailValid(email) || loading}
      />
      <div className="auth__license">
        <div>
          Нажимая на&nbsp;кнопку «Получить код в&nbsp;письме»,
          <br />
          даю
          {" "}
          <a href="/account-agree.pdf" target="_blank" rel="noreferrer">
            согласие
          </a>
          {" "}
          на обработку моих персональных данных

          <br />
          в соответствии с
          {" "}
          <a href="/privacy-policy" target="_blank" rel="noreferrer">
            Политикой конфиденциальности
          </a>
        </div>
      </div>
      <div className="auth__text">
        <b onClick={() =>
          setAuthParamToURL(location, FormTypeEnum.LoginAsExpert)}
        >
          Войти как эксперт
        </b>
      </div>
    </>
  );
};

export default LoginByEmailModal;
